import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Bell,
  BookOpen,
  Bot,
  FolderKanban,
  Kanban,
  LayoutDashboard,
  LogOut,
  MailPlus,
  Menu,
  Rocket,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  TextSearch,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { ApiError, api } from '../api/client';
import { meQueryKey, useMe } from '../api/session';
import type { ProjectDetail } from '../api/types';
import { Breadcrumb } from '../ui/Breadcrumb';
import { Avatar } from '../ui/misc';
import { useActiveProject } from './activeProject';
import { CrumbProvider, useCurrentCrumbs } from './crumbs';

interface NavEntry {
  label: string;
  to: string;
  icon: LucideIcon;
  end?: boolean;
}

function NavGroup({ title, items, action }: { title: string; items: NavEntry[]; action?: ReactNode }) {
  return (
    <div className="nav-group">
      <div className="nav-group-title" title={title}>
        <span className="nav-group-label">{title}</span>
        {action}
      </div>
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} className="nav-item">
          <it.icon size={16} aria-hidden />
          <span>{it.label}</span>
        </NavLink>
      ))}
    </div>
  );
}

export function Wordmark({ size = 26 }: { size?: number }) {
  return (
    <span className="wordmark" style={{ fontSize: size }}>
      Lixionary Jira<span className="dot">.</span>
    </span>
  );
}

function Sidebar({ open }: { open: boolean }) {
  const me = useMe();
  // The current project, or the last one opened, so its menu stays put on workspace and admin pages.
  const active = useActiveProject(me.user.id);
  const key = active.key;
  const project = useQuery({
    queryKey: ['project', key],
    queryFn: () => api.get<ProjectDetail>(`/projects/${key}`),
    enabled: !!key,
    retry: false,
  });
  const lostAccess =
    project.error instanceof ApiError && (project.error.status === 404 || project.error.status === 403);
  const { remembered, remember, forget } = active;
  const loadedKey = project.data?.key;
  useEffect(() => {
    if (remembered && lostAccess) forget();
    else if (!remembered && loadedKey) remember(loadedKey);
  }, [remembered, lostAccess, loadedKey, remember, forget]);
  const admin = me.user.role === 'admin';
  const base = `/projects/${key}`;
  return (
    <aside className={`sidebar${open ? ' open' : ''}`} aria-label="Main navigation">
      <Wordmark />
      <nav className="stack lg" style={{ gap: 24 }}>
        <NavGroup
          title="Workspace"
          items={[
            { label: 'Projects', to: '/projects', icon: FolderKanban, end: true },
            { label: 'Search', to: '/search', icon: Search },
            { label: 'Notifications', to: '/notifications', icon: Bell },
            { label: 'Approvals', to: '/approvals', icon: ShieldCheck },
            { label: 'My AI connections', to: '/account/ai-connections', icon: Bot },
          ]}
        />
        {key && !lostAccess && (
          <NavGroup
            title={project.data ? `${project.data.name} · ${project.data.key}` : key}
            action={
              remembered ? (
                <button
                  type="button"
                  className="nav-group-close"
                  onClick={forget}
                  aria-label={`Hide ${project.data?.name ?? key} from the sidebar`}
                  title="Hide project menu"
                >
                  <X size={14} aria-hidden />
                </button>
              ) : undefined
            }
            items={[
              { label: 'Dashboard', to: base, icon: LayoutDashboard, end: true },
              { label: 'Board', to: `${base}/board`, icon: Kanban },
              { label: 'Search', to: `${base}/search`, icon: TextSearch },
              { label: 'Releases', to: `${base}/releases`, icon: Rocket },
              { label: 'Context', to: `${base}/context/overview`, icon: BookOpen },
              { label: 'Activity', to: `${base}/activity`, icon: Activity },
              ...(admin ? [{ label: 'Settings', to: `${base}/settings`, icon: Settings }] : []),
            ]}
          />
        )}
        {admin && (
          <NavGroup
            title="Administration"
            items={[
              { label: 'Users', to: '/admin/users', icon: Users },
              { label: 'Invitations', to: '/admin/invitations', icon: MailPlus },
              { label: 'AI agents', to: '/admin/ai-agents', icon: Bot },
              { label: 'Ticket assistant', to: '/admin/assistant', icon: Sparkles },
              { label: 'Audit', to: '/admin/audit', icon: ScrollText },
            ]}
          />
        )}
      </nav>
    </aside>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const me = useMe();
  const crumbs = useCurrentCrumbs();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const signOut = async () => {
    const r = await api
      .post<{ endSessionUrl: string | null }>('/auth/logout')
      .catch(() => ({ endSessionUrl: null }));
    sessionStorage.removeItem('lj_had_session');
    qc.clear();
    if (r.endSessionUrl) window.location.assign(r.endSessionUrl);
    else navigate('/sign-in?signedOut=1');
  };
  const unread = me.unreadNotifications;
  return (
    <header className="topbar">
      <button className="icon-btn menu-button" onClick={onMenu} aria-label="Open navigation">
        <Menu size={18} />
      </button>
      <div className="grow">
        <Breadcrumb items={crumbs} />
      </div>
      <NavLink to="/search" className="search-trigger" aria-label="Search tickets (press /)">
        <Search size={14} aria-hidden />
        <span className="label" style={{ flex: 1 }}>
          Search tickets
        </span>
        <span className="kbd label">/</span>
      </NavLink>
      <NavLink to="/notifications" className="icon-link" aria-label={`Notifications, ${unread} unread`}>
        <Bell size={18} aria-hidden />
        {unread > 0 && <span className="count-pill">{unread > 99 ? '99+' : unread}</span>}
      </NavLink>
      <div className="row nowrap" style={{ gap: 10 }}>
        <Avatar name={me.user.displayName} />
        <div className="user-name" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <span className="small strong">{me.user.displayName}</span>
          <span style={{ fontSize: 12 }} className="muted">
            {me.user.role === 'admin' ? 'Admin' : 'Member'}
          </span>
        </div>
        <button className="icon-btn" onClick={signOut} aria-label="Sign out" title="Sign out">
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}

export function AppShell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    // Keep unread counts and session state fresh on navigation.
    void qc.invalidateQueries({ queryKey: meQueryKey });
  }, [location.pathname, qc]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) && !t.isContentEditable) {
        e.preventDefault();
        navigate('/search');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
  return (
    <CrumbProvider>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <div className="shell">
        <Sidebar open={open} />
        <div className="main-col">
          <DevAuthBanner />
          <TopBar onMenu={() => setOpen((o) => !o)} />
          <main id="main" tabIndex={-1} style={{ outline: 'none' }}>
            <Outlet />
          </main>
        </div>
      </div>
    </CrumbProvider>
  );
}

function DevAuthBanner() {
  const me = useMe();
  if (!me.devAuth) return null;
  return (
    <div
      role="status"
      style={{
        background: 'rgba(212,160,23,0.16)',
        color: '#785b0e',
        fontSize: 12,
        fontWeight: 500,
        padding: '4px 32px',
        borderBottom: '1px solid var(--color-hairline)',
      }}
    >
      Development sign-in is enabled on this server (DEV_AUTH_ENABLED). Never use this configuration in
      production.
    </div>
  );
}
